import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, FormControl, Select, MenuItem, TextField,
  CircularProgress, Alert, Tooltip, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import { Search, Refresh, Description, Receipt, CheckCircle, Cancel, HourglassEmpty, Download, ChevronLeft, ChevronRight, AccountBalanceWallet, Visibility, Close } from '@mui/icons-material';
import {
  getPayoutReports,
  getQrTransactionReports,
  getGatewayAepsReports,
  getGatewayUpiAtmReports,
  getGatewayCreditCardReports,
  getGatewayBbpsReports,
  getGatewayEpBbpsReports,
  getGatewayTransactionsByApiCode,
  getGatewayBalance
} from '../../services/api';
import { showWarningToast, showErrorToast, showSuccessToast } from '../../utils/toast';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount ?? 0);

const fmtDate = (d) => {
  if (!d) return '—';
  return formatDateDDMMYYYY(d);
};

// Status chip
const StatusChip = ({ status }) => {
  const map = {
    SUCCESS: { color: '#166534', bg: '#dcfce7', icon: <CheckCircle sx={{ fontSize: 12 }} /> },
    FAILED: { color: '#991b1b', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
    REVERSED: { color: '#991b1b', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
    REJECTED: { color: '#991b1b', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
    INITIATED: { color: '#92400e', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
    PENDING: { color: '#92400e', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
    SETTLEMENT_PENDING: { color: '#92400e', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
    SETTLED: { color: '#166534', bg: '#dcfce7', icon: <CheckCircle sx={{ fontSize: 12 }} /> },
    EXPIRED: { color: '#991b1b', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
  };
  const s = map[status?.toUpperCase()] || { color: '#374151', bg: '#f3f4f6', icon: null };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.5, py: 0.4, borderRadius: 99,
      backgroundColor: s.bg, color: s.color,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    }}>
      {s.icon}{status || 'N/A'}
    </Box>
  );
};

// Empty state
const Empty = ({ label }) => (
  <Paper
    variant="outlined"
    sx={{
      textAlign: 'center',
      py: 10,
      px: 4,
      color: '#64748b',
      borderRadius: 4,
      backgroundColor: '#f8fafc',
      border: '1px dashed #cbd5e1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      maxWidth: 500,
      mx: 'auto',
      mt: 4
    }}
  >
    <Box sx={{
      width: 64,
      height: 64,
      borderRadius: '50%',
      backgroundColor: '#f1f5f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#94a3b8',
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
    }}>
      <Receipt sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 600, color: '#334155', fontSize: '1rem' }}>No Data Loaded</Typography>
    <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 300, lineHeight: 1.5 }}>
      {label}
    </Typography>
  </Paper>
);

// Table wrapper
const DataTable = ({ columns, rows, keyField = 'id' }) => (
  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: columns.length > 5 ? 1000 : 650 }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
          {columns.map(c => (
            <TableCell key={c.key} sx={{ fontWeight: 600, fontSize: 11, letterSpacing: 0.5, color: '#4b5563', textTransform: 'uppercase', py: 1.5, ...(c.align ? { textAlign: c.align } : {}) }}>
              {c.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={row[keyField] ?? idx} hover sx={{ '&:last-child td': { border: 0 } }}>
            {columns.map(c => (
              <TableCell key={c.key} sx={{ fontSize: 13, py: 1.5, color: '#1f2937', ...(c.align ? { textAlign: c.align } : {}) }}>
                {c.render ? c.render(row) : (row[c.key] ?? '—')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ServicesReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [serviceCode, setServiceCode] = useState('payout');
  const [status, setStatus] = useState('ALL');
  const [apiCode, setApiCode] = useState('');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  const [selectedTxn, setSelectedTxn] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const PAGE_SIZE = 10;

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await getGatewayBalance();
      if (response && response.data) {
        setBalanceData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch gateway balance:', err);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchReport = async (targetPage = 1) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffTime = Math.abs(to - from);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (to < from) {
      const msg = 'To Date cannot be earlier than From Date.';
      setError(msg);
      showErrorToast(msg);
      return;
    }
    if (diffDays > 30) {
      const msg = 'Date range cannot exceed 30 days. For older data, please contact the administrator.';
      setError(msg);
      showWarningToast(msg);
      return;
    }

    setLoading(true);
    setError('');

    try {
      let data = [];

      const handleResponse = (resData) => {
        if (resData?.status === 'FAILED' || resData?.status === 'error') {
          const apiErrorMsg = resData.message || 'Failed to load reports.';
          setError(apiErrorMsg);
          showErrorToast(apiErrorMsg);
          setReports([]);
          setReportSummary(null);
          setTotalRecords(0);
          setTotalPages(0);
          setLoading(false);
          return null;
        }
        let d = [];
        if (resData && typeof resData === 'object' && !Array.isArray(resData)) {
          // Handle new nested QR response format (merchant + transaction structure)
          if (resData.data && Array.isArray(resData.data) && resData.data[0]?.merchant && resData.data[0]?.transaction) {
            d = resData.data.map(item => ({
              ...item.transaction,
              settled: item.transaction?.settlementStatus === 'SETTLED'
            }));
            setTotalRecords(resData.totalRecords || d.length);
            setTotalPages(Math.ceil((resData.totalRecords || d.length) / PAGE_SIZE) || 1);
            setReportSummary(resData.summary || {
              totalAmount: resData.summary?.totalAmount || 0,
              totalCharges: resData.summary?.totalCharges || 0,
              totalGst: resData.summary?.totalGst || 0,
              totalDeduction: 0,
              totalTransactions: resData.totalRecords || 0,
            });
          } else {
            // Handle legacy flat array format
            d = resData.transactions || resData.content || resData.data || [];
            setTotalRecords(resData.totalRecords || resData.total || resData.totalElements || d.length);
            setTotalPages(resData.totalPages || Math.ceil((resData.totalRecords || d.length) / PAGE_SIZE) || 1);
            setReportSummary({
              totalAmount: resData.totalAmount || 0,
              totalCharges: resData.totalCharges || 0,
              totalGst: resData.totalGst || 0,
              totalDeduction: resData.totalDeduction || 0,
              totalTransactions: resData.totalTransactions || 0,
            });
          }
        } else if (Array.isArray(resData)) {
          d = resData;
          setTotalRecords(d.length);
          setTotalPages(Math.ceil(d.length / PAGE_SIZE));
          setReportSummary(null);
        }
        return d;
      };

      if (serviceCode === 'payout') {
        const response = await getPayoutReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'qr') {
        const response = await getQrTransactionReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'aeps') {
        const response = await getGatewayAepsReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'upiatm') {
        const response = await getGatewayUpiAtmReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'creditcard') {
        const response = await getGatewayCreditCardReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'bbps') {
        const response = await getGatewayBbpsReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'epbbps') {
        const response = await getGatewayEpBbpsReports(fromDate, toDate, targetPage, PAGE_SIZE, status);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else {
        data = [];
        setTotalRecords(0);
        setTotalPages(0);
        setReportSummary(null);
      }

      setReports(data);
      setPage(targetPage);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      const apiErrorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to load reports. Please try again.';
      setError(apiErrorMsg);
      showErrorToast(apiErrorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear data when service changes (don't auto-fetch, just clear)
    setReports([]);
    setReportSummary(null);
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    setError('');
    setStatus('ALL');
  }, [serviceCode]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      if (reports.length > PAGE_SIZE) {
        setPage(newPage);
      } else {
        fetchReport(newPage);
      }
    }
  };

  const exportData = async () => {
    if (!reports || reports.length === 0) {
      showWarningToast('No data to export. Please fetch the report first.');
      return;
    }

    setLoading(true);
    showSuccessToast('Preparing export, please wait...');

    try {
      let allRecords = [];
      const tempPageSize = 50;
      const maxExportRecords = 1000;

      const fetchAllData = async (p, size) => {
        if (serviceCode === 'payout') {
          return await getPayoutReports(fromDate, toDate, p, size);
        } else if (serviceCode === 'qr') {
          return await getQrTransactionReports(fromDate, toDate, p, size);
        } else if (serviceCode === 'aeps') {
          return await getGatewayAepsReports(fromDate, toDate, p, size);
        } else if (serviceCode === 'upiatm') {
          return await getGatewayUpiAtmReports(fromDate, toDate, p, size);
        } else if (serviceCode === 'creditcard') {
          return await getGatewayCreditCardReports(fromDate, toDate, p, size);
        } else if (serviceCode === 'bbps') {
          return await getGatewayBbpsReports(fromDate, toDate, p, size);
        } else if (serviceCode === 'epbbps') {
          return await getGatewayEpBbpsReports(fromDate, toDate, p, size, status);
        }
        return { data: { transactions: [], data: [] } };
      };

      // Fetch data page by page for export
      for (let p = 1; p <= totalPages; p++) {
        if (allRecords.length >= maxExportRecords) break;

        const response = await fetchAllData(p, tempPageSize);
        const resData = response.data;

        // Handle new nested QR response format
        let pageRecords = [];
        if (resData.data && Array.isArray(resData.data) && resData.data[0]?.merchant && resData.data[0]?.transaction) {
          pageRecords = resData.data.map(item => item.transaction);
        } else {
          pageRecords = resData.transactions || resData.content || resData.data || [];
        }

        if (pageRecords.length === 0) break;
        allRecords = [...allRecords, ...pageRecords];

        if (allRecords.length >= (resData.totalRecords || resData.total || 0)) break;
      }

      if (allRecords.length > maxExportRecords) {
        allRecords = allRecords.slice(0, maxExportRecords);
        showWarningToast(`Export limited to the first ${maxExportRecords} records.`);
      }

      const escapeCsv = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

      let headers, csvRows;
      if (serviceCode === 'qr') {
        headers = [
          'Date', 'Order ID', 'Receipt', 'Payer VPA', 'Payer Name',
          'Transaction ID', 'Txn ID',
          'Amount', 'Charge', 'GST', 'Net Amount',
          'Status', 'Settlement Status', 'Customer Name', 'Customer Mobile'
        ];
        csvRows = allRecords.map(t => [
          formatDateTimeDDMMYYYY(t.createdAt || t.date),
          t.orderId || t.receipt || '-',
          t.receipt || '-',
          t.payerVpa || '-',
          t.payerName || '-',
          t.transactionId || '-',
          t.digiKhataTxnId || '-',
          t.amount || 0,
          t.chargeAmount || 0,
          t.gstAmount || 0,
          t.netAmount || 0,
          t.status || 'UNKNOWN',
          t.settlementStatus || 'UNKNOWN',
          t.customerName || t.payerName || '-',
          t.customerMobile || '-'
        ].map(escapeCsv));
      } else if (serviceCode === 'payout') {
        headers = [
          'Date', 'Service', 'Beneficiary Name', 'Bank Name', 'Account Number', 'IFSC',
          'Request ID', 'Transaction ID', 'UTR',
          'Base Amount', 'Charge Amount', 'GST Amount', 'Net Deduction',
          'Opening Balance', 'Closing Balance', 'Status'
        ];
        csvRows = allRecords.map(t => [
          formatDateTimeDDMMYYYY(t.createdAt || t.date),
          serviceCode || 'payout',
          t.beneficiaryName || '-',
          t.bankName || '-',
          t.accountNumber ? `\t${t.accountNumber}` : '-',
          t.ifscCode || '-',
          t.requestId || '-',
          t.transactionId || t.txnId || '-',
          t.status === 'SUCCESS' ? (t.utr || '-') : '-',
          t.amount || 0,
          t.chargeAmount || 0,
          t.gstAmount || 0,
          t.totalDeduction || 0,
          t.balanceBefore || 0,
          t.balanceAfter || 0,
          t.status || 'UNKNOWN'
        ].map(escapeCsv));
      } else if (serviceCode === 'aeps' || serviceCode === 'upiatm') {
        headers = [
          'Date', 'Transaction ID', 'Reference ID', 'Base Amount', 'Charge', 'GST',
          'Commission', 'TDS', 'Net Amount', 'Status', 'Settlement Status'
        ];
        csvRows = allRecords.map(t => [
          formatDateTimeDDMMYYYY(t.createdAt || t.date),
          t.transactionId || '-',
          t.referenceId || '-',
          t.amount || t.baseAmount || 0,
          t.chargeAmount || 0,
          t.gstAmount || 0,
          t.commissionAmount || 0,
          t.tdsAmount || 0,
          t.netAmount || 0,
          t.status || 'UNKNOWN',
          t.settlementStatus || 'UNKNOWN'
        ].map(escapeCsv));
      } else if (serviceCode === 'epbbps') {
        headers = [
          'Date', 'Transaction ID', 'Order ID', 'Operator Ref / ID',
          'Service Type', 'Consumer / Mobile No', 'Consumer Name',
          'Base Amount', 'Charge Amount', 'GST Amount', 'Commission', 'Total Deduction',
          'Opening Balance', 'Closing Balance', 'Status', 'Error Message'
        ];
        csvRows = allRecords.map(t => [
          formatDateTimeDDMMYYYY(t.createdAt || t.date || t.timestamp),
          t.transactionId || '-',
          t.orderId || '-',
          t.operatorId || '-',
          t.serviceType || 'RECHARGE',
          t.consumerNo || t.mobileNumber || '-',
          t.consumerName || '-',
          t.amount || 0,
          t.chargeAmount || 0,
          t.gstAmount || 0,
          (t.amount != null && t.totalDeduction != null && t.amount > t.totalDeduction) ? Number((t.amount - t.totalDeduction).toFixed(2)) : 0,
          t.totalDeduction ?? t.amount ?? 0,
          t.balanceBefore ?? t.openingBalance ?? '-',
          t.balanceAfter ?? t.closingBalance ?? '-',
          t.status || 'UNKNOWN',
          t.errorMessage || '-'
        ].map(escapeCsv));
      } else {
        headers = [
          'Date', 'Transaction ID', 'API Code', 'Reference ID', 'Type',
          'Base Amount', 'Charge', 'GST', 'Commission', 'TDS', 'Net Amount',
          'Opening Balance', 'Closing Balance', 'Description', 'Remarks'
        ];
        csvRows = allRecords.map(t => [
          formatDateTimeDDMMYYYY(t.createdAt || t.date),
          t.transactionId || '-',
          t.apiCode || serviceCode,
          t.referenceId || '-',
          t.transactionType || '-',
          t.baseAmount || t.amount || 0,
          t.chargeAmount || 0,
          t.gstAmount || 0,
          t.commissionAmount || 0,
          t.tdsAmount || 0,
          t.netAmount || 0,
          t.openingBalance || 0,
          t.closingBalance || 0,
          t.description || '-',
          t.remarks || '-'
        ].map(escapeCsv));
      }

      const csvContent = [headers.join(','), ...csvRows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `services-report-${serviceCode}-${fromDate}-to-${toDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showSuccessToast(`Exported ${allRecords.length} records successfully!`);
    } catch (err) {
      console.error('Export failed:', err);
      showErrorToast('Failed to export data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTableColumns = () => {
    if (serviceCode === 'qr') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        {
          key: 'refs', label: 'Order ID / Receipt', render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Order: {r.orderId || r.receipt || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Ref: {r.referenceId || '—'}</Typography>
            </Box>
          )
        },
        {
          key: 'payer', label: 'Payer Details', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.payerName || r.customerName || 'N/A'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#7c3aed', display: 'block' }}>{r.payerVpa || '—'}</Typography>
            </Box>
          )
        },
        { key: 'rrn', label: 'Txn ID ', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#374151', fontWeight: 600 }}>{r.transactionId || r.digiKhataTxnId || '—'}</Typography> },
        {
          key: 'amount', label: 'Amount & Charges', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        { key: 'netAmount', label: 'Net Amount', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>{fmt(r.netAmount)}</Typography> },
        { key: 'txnStatus', label: 'Transaction Status', align: 'center', render: r => <StatusChip status={r.status} /> },
        { key: 'settlementStatus', label: 'Settlement Status', align: 'center', render: r => <StatusChip status={r.settlementStatus} /> },
      ];
    }

    if (serviceCode === 'payout') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        {
          key: 'beneficiary', label: 'Beneficiary', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.beneficiaryName || 'N/A'}</Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>{r.bankName || 'No Bank Name'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#4b5563' }}>Acc: {r.accountNumber || '-'}</Typography>
            </Box>
          )
        },
        {
          key: 'transactionId', label: 'Req / Txn Details', render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Req: {r.requestId || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Txn: {r.transactionId || r.txnId || '—'}</Typography>
            </Box>
          )
        },
        { key: 'utr', label: 'UTR', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#374151', fontWeight: 600 }}>{r.status === 'SUCCESS' ? (r.utr || '—') : '—'}</Typography> },
        {
          key: 'breakdown', label: 'Base & Charges', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        { key: 'netDeduction', label: 'Net Deduction', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#dc2626', fontSize: 13 }}>{fmt(r.totalDeduction)}</Typography> },
        { key: 'balanceBefore', label: 'Opening Balance', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>{fmt(r.balanceBefore)}</Typography> },
        { key: 'balanceAfter', label: 'Closing Balance', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>{fmt(r.balanceAfter)}</Typography> },
        { key: 'status', label: 'Status', align: 'right', render: r => <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}><StatusChip status={r.status} /></Box> },
      ];
    }

    if (serviceCode === 'aeps' || serviceCode === 'upiatm') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        {
          key: 'transactionId', label: 'Txn / Ref ID', render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Txn: {r.transactionId || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Ref: {r.referenceId || '—'}</Typography>
            </Box>
          )
        },
        {
          key: 'breakdown', label: 'Base & Charges', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount || r.baseAmount)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        {
          key: 'commTds', label: 'Comm / TDS', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>+{fmt(r.commissionAmount)}</Typography>
              <Typography variant="caption" sx={{ color: '#dc2626', display: 'block' }}>TDS -{fmt(r.tdsAmount)}</Typography>
            </Box>
          )
        },
        { key: 'netAmount', label: 'Net Amount', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>{fmt(r.netAmount)}</Typography> },
        { key: 'txnStatus', label: 'Status', align: 'center', render: r => <StatusChip status={r.status || r.transactionStatus} /> },
        { key: 'settlementStatus', label: 'Settlement Status', align: 'center', render: r => <StatusChip status={r.settlementStatus} /> },
      ];
    }

    if (serviceCode === 'epbbps') {
      return [
        {
          key: 'createdAt',
          label: 'Date & Time',
          render: r => (
            <Typography variant="caption" sx={{ color: '#4b5563', whiteSpace: 'nowrap' }}>
              {formatDateDDMMYYYY(r.createdAt || r.date)} <br />
              {new Date(r.createdAt || r.date || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </Typography>
          )
        },
        {
          key: 'transactionId',
          label: 'Txn / Order / Op ID',
          render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>
                Txn: {r.transactionId || '—'}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>
                Order: {r.orderId || '—'}
              </Typography>
              {r.operatorId ? (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#2563eb', display: 'block' }}>
                  Op ID: {r.operatorId}
                </Typography>
              ) : null}
            </Box>
          )
        },
        {
          key: 'consumer',
          label: 'Consumer Details',
          render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>
                {r.consumerNo || r.mobileNumber || '—'}
              </Typography>
              {r.consumerName && (
                <Typography variant="caption" sx={{ color: '#4b5563', display: 'block' }}>
                  {r.consumerName}
                </Typography>
              )}
            </Box>
          )
        },
        {
          key: 'serviceType',
          label: 'Service',
          render: r => (
            <Box>
              <Chip
                label={r.serviceType || 'RECHARGE'}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: 10, fontWeight: 700, borderRadius: 1 }}
              />
            </Box>
          )
        },
        {
          key: 'amount',
          label: 'Amount & Charges',
          align: 'right',
          render: r => {
            const comm = (r.amount != null && r.totalDeduction != null && r.amount > r.totalDeduction)
              ? (r.amount - r.totalDeduction)
              : 0;
            const charges = (r.chargeAmount || 0) + (r.gstAmount || 0);
            return (
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount)}</Typography>
                {comm > 0 ? (
                  <Typography variant="caption" sx={{ color: '#16a34a', display: 'block', fontWeight: 600 }}>
                    Comm: +{fmt(comm)}
                  </Typography>
                ) : charges > 0 ? (
                  <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>
                    Chg {fmt(r.chargeAmount || 0)} + GST {fmt(r.gstAmount || 0)}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                    Chg: ₹0.00
                  </Typography>
                )}
              </Box>
            );
          }
        },
        {
          key: 'totalDeduction',
          label: 'Net Deduction',
          align: 'right',
          render: r => (
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#dc2626', fontSize: 13 }}>
                {fmt(r.totalDeduction ?? r.amount)}
              </Typography>
            </Box>
          )
        },
        {
          key: 'balances',
          label: 'Balances (Op/Cl)',
          align: 'right',
          render: r => (
            <Box>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Op: {r.balanceBefore !== undefined && r.balanceBefore !== null ? fmt(r.balanceBefore) : (r.openingBalance !== undefined ? fmt(r.openingBalance) : '—')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Cl: {r.balanceAfter !== undefined && r.balanceAfter !== null ? fmt(r.balanceAfter) : (r.closingBalance !== undefined ? fmt(r.closingBalance) : '—')}
              </Typography>
            </Box>
          )
        },
        {
          key: 'status',
          label: 'Status',
          align: 'center',
          render: r => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <StatusChip status={r.status || 'PENDING'} />
              {r.errorMessage && (
                <Tooltip title={r.errorMessage} arrow>
                  <Typography variant="caption" sx={{ color: '#dc2626', maxWidth: 120, display: 'block', cursor: 'pointer' }} noWrap>
                    {r.errorMessage}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          )
        },
        {
          key: 'actions',
          label: 'Action',
          align: 'center',
          render: r => (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedTxn(r)}
              startIcon={<Visibility sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none',
                borderRadius: 1.5,
                fontSize: 11,
                py: 0.3,
                px: 1,
                color: '#10b981',
                borderColor: '#10b981',
                '&:hover': { borderColor: '#059669', bgcolor: '#f0fdf4' }
              }}
            >
              Details
            </Button>
          )
        },
      ];
    }

    // Default: Credit Card, BBPS, Generic API Code
    return [
      { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
      {
        key: 'transactionId', label: 'Txn / Ref ID', render: r => (
          <Box>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Txn: {r.transactionId || '—'}</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Ref: {r.referenceId || '—'}</Typography>
          </Box>
        )
      },
      {
        key: 'type', label: 'Type', render: r => (
          <Chip
            label={r.transactionType || 'DEBIT'}
            size="small"
            color={r.transactionType === 'CREDIT' ? 'success' : 'error'}
            variant="outlined"
            sx={{ fontSize: 10, fontWeight: 700 }}
          />
        )
      },
      {
        key: 'breakdown', label: 'Base & Charges', align: 'right', render: r => (
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.baseAmount || r.amount)}</Typography>
            <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
          </Box>
        )
      },
      {
        key: 'commTds', label: 'Comm / TDS', align: 'right', render: r => (
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>+{fmt(r.commissionAmount)}</Typography>
            <Typography variant="caption" sx={{ color: '#dc2626', display: 'block' }}>TDS -{fmt(r.tdsAmount)}</Typography>
          </Box>
        )
      },
      {
        key: 'netAmount', label: 'Net Amount', align: 'right', render: r => (
          <Typography sx={{ fontWeight: 600, color: r.transactionType === 'CREDIT' ? '#16a34a' : '#dc2626', fontSize: 13 }}>
            {fmt(r.netAmount)}
          </Typography>
        )
      },
      {
        key: 'balances', label: 'Balances (Op/Cl)', align: 'right', render: r => (
          <Box>
            <Typography variant="caption" sx={{ display: 'block' }}>Op: {fmt(r.openingBalance)}</Typography>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>Cl: {fmt(r.closingBalance)}</Typography>
          </Box>
        )
      },
      {
        key: 'remarks', label: 'Desc / Remarks', render: r => (
          <Box sx={{ maxWidth: 150 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#4b5563' }} noWrap>{r.description || '—'}</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }} noWrap>{r.remarks || '—'}</Typography>
          </Box>
        )
      },
      { key: 'status', label: 'Status', align: 'center', render: r => <StatusChip status={r.status || 'SUCCESS'} /> },
    ];
  };

  const startRecord = (page - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(page * PAGE_SIZE, totalRecords || reports.length);

  return (
    <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Services Report</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Detailed breakdown of all service transactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={exportData}
            disabled={loading || reports.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#10b981', borderColor: '#10b981', '&:hover': { borderColor: '#059669', bgcolor: '#f0fdf4' } }}
          >
            Export CSV
          </Button>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={() => {
                fetchBalance();
                fetchReport(page);
              }}
              disabled={loading || balanceLoading}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, color: '#10b981', '&:hover': { bgcolor: '#f0fdf4', borderColor: '#10b981' } }}
            >
              <Refresh sx={{ fontSize: 18, ...((loading || balanceLoading) && { animation: 'spin 1s linear infinite' }) }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filter Bar ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: '#e2e8f0', borderRadius: 3, mb: 4, backgroundColor: '#f8fafc' }}>
        <CardContent sx={{ p: '24px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Description sx={{ fontSize: 18, color: '#64748b' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569' }}>Report Filters</Typography>
          </Box>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={serviceCode === 'epbbps' ? 2.5 : 3}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>From Date</Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: '#10b981' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={serviceCode === 'epbbps' ? 2.5 : 3}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>To Date</Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: '#10b981' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={serviceCode === 'epbbps' ? 2.5 : 3}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Services</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={serviceCode}
                  onChange={e => setServiceCode(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    fontSize: 13,
                    backgroundColor: '#ffffff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981' }
                  }}
                >
                  <MenuItem value="payout">Payout</MenuItem>
                  <MenuItem value="qr">QR Pay-In</MenuItem>
                  <MenuItem value="aeps">AEPS</MenuItem>
                  <MenuItem value="upiatm">UPI-ATM</MenuItem>
                  <MenuItem value="creditcard">Credit Card</MenuItem>
                  {/* <MenuItem value="bbps">BBPS</MenuItem> */}
                  <MenuItem value="epbbps">BBPS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {serviceCode === 'epbbps' && (
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={status}
                    onChange={e => setStatus(e.target.value)}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      fontSize: 13,
                      backgroundColor: '#ffffff',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981' }
                    }}
                  >
                    <MenuItem value="ALL">All Statuses</MenuItem>
                    <MenuItem value="PENDING">PENDING</MenuItem>
                    <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                    <MenuItem value="FAILED">FAILED</MenuItem>
                    <MenuItem value="REVERSED">REVERSED</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={serviceCode === 'epbbps' ? 2.5 : 3}>
              <Box sx={{ display: 'flex', gap: 1.5, height: '40px' }}>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<Search sx={{ fontSize: 16 }} />}
                  onClick={() => fetchReport(1)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    flex: 1,
                    boxShadow: 'none',
                    backgroundColor: '#10b981',
                    '&:hover': { backgroundColor: '#059669', boxShadow: 'none' }
                  }}
                >
                  Fetch
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => {
                    setFromDate(new Date().toISOString().split('T')[0]);
                    setToDate(new Date().toISOString().split('T')[0]);
                    setServiceCode('payout');
                    setApiCode('');
                    setStatus('ALL');
                    setReports([]);
                    setReportSummary(null);
                    setPage(1);
                    setTotalPages(0);
                    setTotalRecords(0);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    flex: 1,
                    color: '#64748b',
                    borderColor: '#cbd5e1',
                    '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f1f5f9' }
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Loading overlay */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* ── Report Data Table ── */}
      {!loading && reports.length > 0 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: '0 !important' }}>
            <Box sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: serviceCode === 'qr' ? '#faf5ff' : serviceCode === 'payout' ? '#f0fdf4' : serviceCode === 'epbbps' ? '#f0fdfa' : '#f8fafc'
            }}>
              <Chip
                label={
                  serviceCode === 'qr' ? 'QR Pay-In' :
                    serviceCode === 'payout' ? 'Payout' :
                      serviceCode === 'aeps' ? 'AEPS' :
                        serviceCode === 'upiatm' ? 'UPI-ATM' :
                          serviceCode === 'creditcard' ? 'Credit Card' :
                            serviceCode === 'bbps' ? 'BBPS' :
                              serviceCode === 'epbbps' ? 'BBPS' : `API Code: ${apiCode || 'General'}`
                }
                size="small"
                sx={{
                  bgcolor:
                    serviceCode === 'qr' ? '#7c3aed' :
                      serviceCode === 'payout' ? '#16a34a' :
                        serviceCode === 'aeps' ? '#0284c7' :
                          serviceCode === 'upiatm' ? '#ea580c' :
                            serviceCode === 'creditcard' ? '#4f46e5' :
                              serviceCode === 'epbbps' ? '#0d9488' : '#475569',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 11
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {
                  serviceCode === 'qr' ? 'QR Transaction Report' :
                    serviceCode === 'payout' ? 'Payout Transactions Report' :
                      serviceCode === 'aeps' ? 'AEPS Transactions Report' :
                        serviceCode === 'upiatm' ? 'UPI-ATM Transactions Report' :
                          serviceCode === 'creditcard' ? 'Credit Card Transactions Report' :
                            serviceCode === 'bbps' ? 'BBPS Transactions Report' :
                              serviceCode === 'epbbps' ? 'BBPS Transactions Report' : 'API Transaction Report'
                }
              </Typography>
            </Box>
            <DataTable
              rows={reports.length > PAGE_SIZE ? reports.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : reports}
              columns={getTableColumns()}
            />
            {/* Pagination Controls */}
            {totalPages > 0 && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#f9fafb', gap: 2 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 13 }}>
                  {totalRecords > 0 ? `Showing ${startRecord} to ${endRecord} of ${totalRecords}` : `Showing ${reports.length} records`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflowX: 'auto', maxWidth: '100%', py: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
                  >
                    <ChevronLeft fontSize="small" />
                  </IconButton>

                  {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => {
                    const p = i + 1;
                    if (totalPages <= 7 || p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
                      return (
                        <Button
                          key={p}
                          variant={p === page ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handlePageChange(p)}
                          sx={{
                            minWidth: 32, width: 32, height: 32, p: 0,
                            ...(p !== page && { border: '1px solid', borderColor: 'divider', color: '#374151', bgcolor: '#fff' }),
                            ...(p === page && { boxShadow: 'none' })
                          }}
                        >
                          {p}
                        </Button>
                      );
                    }
                    if (p === 2 || p === totalPages - 1) return <Typography key={p} sx={{ px: 0.5, color: '#9ca3af' }}>...</Typography>;
                    return null;
                  })}

                  <IconButton
                    size="small"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages || totalPages === 0}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
                  >
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      {!loading && reports.length === 0 && <Empty label="Click 'Fetch' to load data for the selected service" />}

      {/* ── Transaction Details Dialog ── */}
      <Dialog
        open={!!selectedTxn}
        onClose={() => setSelectedTxn(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2.5, sm: 3 },
            width: '100%',
            maxWidth: 640,
            m: { xs: 1.5, sm: 2 },
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#10b981',
                border: '1px solid #d1fae5'
              }}
            >
              <Receipt fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#0f172a', lineHeight: 1.2 }}>
                Transaction Details
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.75rem' }}>
                ID: {selectedTxn?.transactionId}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedTxn && <StatusChip status={selectedTxn.status} />}
            <IconButton size="small" onClick={() => setSelectedTxn(null)} sx={{ color: '#94a3b8', '&:hover': { color: '#475569', bgcolor: '#f1f5f9' } }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 }, overflowY: 'auto' }}>
          {selectedTxn && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              {/* Summary 4-Metrics Cards (100% width, no blank space) */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                  gap: 1.25,
                  width: '100%'
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1, sm: 1.25 },
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                    borderColor: '#e2e8f0',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: { xs: '0.68rem', sm: '0.74rem' }, display: 'block' }}>
                    Recharge Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.25, fontSize: { xs: '0.95rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                    {fmt(selectedTxn.amount)}
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1, sm: 1.25 },
                    borderRadius: 2,
                    bgcolor: '#fffbeb',
                    borderColor: '#fef3c7',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600, fontSize: { xs: '0.68rem', sm: '0.74rem' }, display: 'block' }}>
                    Charges + GST
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#d97706', mt: 0.25, fontSize: { xs: '0.95rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                    {fmt((selectedTxn.chargeAmount || 0) + (selectedTxn.gstAmount || 0))}
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1, sm: 1.25 },
                    borderRadius: 2,
                    bgcolor: '#f0fdf4',
                    borderColor: '#bbf7d0',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600, fontSize: { xs: '0.68rem', sm: '0.74rem' }, display: 'block' }}>
                    Commission
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a', mt: 0.25, fontSize: { xs: '0.95rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                    {selectedTxn.amount > (selectedTxn.totalDeduction ?? selectedTxn.amount)
                      ? fmt(selectedTxn.amount - selectedTxn.totalDeduction)
                      : fmt(0)}
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1, sm: 1.25 },
                    borderRadius: 2,
                    bgcolor: '#ecfdf5',
                    borderColor: '#a7f3d0',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#047857', fontWeight: 600, fontSize: { xs: '0.68rem', sm: '0.74rem' }, display: 'block' }}>
                    Total Deduction
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669', mt: 0.25, fontSize: { xs: '0.95rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                    {fmt(selectedTxn.totalDeduction ?? selectedTxn.amount)}
                  </Typography>
                </Paper>
              </Box>

              {/* 2 Detail Columns (100% width, 50% each on tablet/desktop) */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.75,
                  width: '100%',
                  alignItems: 'stretch'
                }}
              >
                {/* Consumer & Request Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75, fontSize: '0.825rem' }}>
                    Consumer & Request Details
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: '#ffffff',
                      borderColor: '#e2e8f0',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: 0.8
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Order ID:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem', color: '#0f172a' }}>
                        {selectedTxn.orderId || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Consumer / Mobile:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a' }}>
                        {selectedTxn.consumerNo || selectedTxn.mobileNumber || '—'}
                      </Typography>
                    </Box>
                    {selectedTxn.consumerName && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Consumer Name:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a' }}>
                          {selectedTxn.consumerName}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Service Type:</Typography>
                      <Chip
                        label={selectedTxn.serviceType || 'RECHARGE'}
                        size="small"
                        sx={{ fontSize: 10, height: 20, fontWeight: 600, bgcolor: '#f1f5f9', color: '#334155' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Operator Ref / ID:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {selectedTxn.operatorId || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Operator Code:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155' }}>
                        {selectedTxn.operatorCode || '—'}
                      </Typography>
                    </Box>
                    {selectedTxn.billValidationId && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Bill Validation ID:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', fontFamily: 'monospace' }}>
                          {selectedTxn.billValidationId}
                        </Typography>
                      </Box>
                    )}
                    {selectedTxn.billDueDate && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Bill Due Date:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155' }}>
                          {formatDateTimeDDMMYYYY(selectedTxn.billDueDate)}
                        </Typography>
                      </Box>
                    )}
                    {(selectedTxn.param1 || selectedTxn.param2) && [selectedTxn.param1, selectedTxn.param2].filter(p => p && p !== 'string').length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Request Params:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155' }}>
                          {[selectedTxn.param1, selectedTxn.param2].filter(p => p && p !== 'string').join(', ')}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Created At:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 500 }}>
                        {formatDateTimeDDMMYYYY(selectedTxn.createdAt)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>

                {/* Wallet & Balance Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75, fontSize: '0.825rem' }}>
                    Wallet & Financial Breakdown
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: '#ffffff',
                      borderColor: '#e2e8f0',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: 0.8
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Opening Balance:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a' }}>
                        {fmt(selectedTxn.balanceBefore)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Base Amount:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a' }}>
                        {fmt(selectedTxn.amount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Charges:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155' }}>
                        {fmt(selectedTxn.chargeAmount || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>GST:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155' }}>
                        {fmt(selectedTxn.gstAmount || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600, fontSize: '0.8rem' }}>Commission:</Typography>
                      <Typography variant="body2" sx={{ color: '#166534', fontWeight: 700, fontSize: '0.82rem' }}>
                        +{fmt(Math.max(0, (selectedTxn.amount || 0) - (selectedTxn.totalDeduction ?? selectedTxn.amount)))}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.25 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: '#fef2f2',
                        p: 0.6,
                        px: 1,
                        borderRadius: 1.5,
                        mx: -0.25
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>Net Wallet Deduction:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#dc2626', fontSize: '0.875rem' }}>
                        {fmt(selectedTxn.totalDeduction ?? selectedTxn.amount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.25 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Closing Balance:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.82rem' }}>
                        {fmt(selectedTxn.balanceAfter)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Updated At:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 500 }}>
                        {formatDateTimeDDMMYYYY(selectedTxn.updatedAt || selectedTxn.createdAt)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>

              {/* Error Message if present */}
              {selectedTxn.errorMessage && (
                <Alert severity="error" sx={{ borderRadius: 2, py: 0.5, fontSize: '0.8rem', width: '100%' }}>
                  <strong>Operator Error Message:</strong> {selectedTxn.errorMessage}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, py: 1.25 }}>
          <Button
            onClick={() => setSelectedTxn(null)}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3.5,
              py: 0.75,
              fontSize: '0.875rem',
              fontWeight: 600,
              backgroundColor: '#10b981',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#059669', boxShadow: 'none' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ServicesReport;
